import { Fragment, useMemo, useState } from 'react'
import { AppShell } from '../components/AppShell'
import {
  apiErrorMessage,
  useAllowedSports,
  useEventMarkets,
  useEvents,
  usePhaseStatus,
  usePlaceBet,
  type MarketEvent,
} from '../lib/api'

interface SlipLeg {
  eventId:       string
  eventLabel:    string
  marketType:    string
  selectionId:   string
  selectionCode: string
  selectionName: string
  oddsDecimal:   string
}

/**
 * Inline markets panel for a single match card. Owns its own /markets fetch
 * (via useEventMarkets) so multiple cards can be expanded simultaneously —
 * needed for the multi-leg parlay flow where the user picks selections from
 * different events.
 */
function MatchMarkets({
  ev, minOdds, maxOdds, legs, onToggleLeg,
}: {
  ev: MarketEvent
  minOdds: number
  maxOdds: number
  legs: SlipLeg[]
  onToggleLeg: (event: MarketEvent, marketType: string, sel: { id: string; name: string; oddsDecimal: string }) => void
}) {
  const markets = useEventMarkets(ev.id)
  return (
    <div className="markets-section" style={{
      marginTop: 'calc(var(--space-3) * -1)',
      padding: 'var(--space-3)',
      border: '1px solid var(--color-primary)',
      borderTop: 'none',
      borderRadius: '0 0 var(--radius-md) var(--radius-md)',
      background: 'var(--bg-surface)',
    }}>
      {markets.isLoading && <div style={{ color: 'var(--text-tertiary)' }}>Loading markets…</div>}
      {!markets.isLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-3)' }}>
          {(markets.data ?? []).map((m) => (
            <div key={m.id}>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)',
                textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)',
                color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>
                {m.type}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {m.selections.map((sel) => {
                  const odds = Number(sel.oddsDecimal)
                  const allowed = odds >= minOdds && odds <= maxOdds
                  const selected = legs.some((l) => l.selectionId === sel.id)
                  return (
                    <button key={sel.id} type="button"
                      className={`odds-button ${selected ? 'odds-button--selected' : ''}`}
                      disabled={!allowed}
                      title={!allowed ? `Outside allowed odds range [${minOdds.toFixed(2)}, ${maxOdds.toFixed(2)}]` : undefined}
                      style={!allowed ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                      onClick={() => onToggleLeg(ev, m.type, sel)}>
                      <span className="odds-button__label">{sel.name}</span>
                      <span className="odds-button__odds">{odds.toFixed(2)}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {markets.data && markets.data.length === 0 && (
            <div style={{ color: 'var(--text-tertiary)' }}>
              {ev.status === 'live'
                ? 'Live in-play odds are not available on the current data plan. Try a pre-match event.'
                : 'No markets for this event.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function fmtSport(sport: string): string {
  // Backend sport codes are snake_case (american_football, ice_hockey). Render
  // them title-cased for the UI without touching the underlying identifier.
  return sport
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IE', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function fmtEur(v: number): string {
  if (!Number.isFinite(v)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
}

function SportIcon({ sport }: { sport: string }) {
  const common = {
    className: 'sport-nav__icon',
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  switch (sport.toLowerCase()) {
    case 'all':        return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M3 12 h18 M12 3 a14 14 0 0 1 0 18 M12 3 a14 14 0 0 0 0 18"/></svg>)
    case 'football':   return (<svg {...common}><circle cx="12" cy="12" r="9"/><polygon points="12,8 15.5,10.5 14,14 10,14 8.5,10.5"/></svg>)
    case 'basketball': return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 3 v18"/><path d="M5.5 5.5 c4 3 4 10 0 13"/><path d="M18.5 5.5 c-4 3 -4 10 0 13"/></svg>)
    case 'baseball':   return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M7 5 q5 7 0 14"/><path d="M17 5 q-5 7 0 14"/></svg>)
    case 'hockey':     return (<svg {...common}><path d="M5 3 l3 14 l4 2"/><path d="M12 19 l8 -2"/><ellipse cx="17" cy="20" rx="3" ry="1.2"/></svg>)
    case 'rugby':      return (<svg {...common}><ellipse cx="12" cy="12" rx="9" ry="5.5" transform="rotate(-30 12 12)"/></svg>)
    case 'volleyball': return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 3 c4 5 4 13 0 18"/><path d="M3 12 c5 -4 13 -4 18 0"/></svg>)
    case 'handball':   return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 4 v16 M4 12 h16"/></svg>)
    default:           return (<svg {...common}><circle cx="12" cy="12" r="9"/></svg>)
  }
}

export function SportsbookPage() {
  const allowedSports = useAllowedSports()
  const events = useEvents({ allowedSports: allowedSports.data })
  const place = usePlaceBet()
  const status = usePhaseStatus()
  const [activeEventIds, setActiveEventIds] = useState<Set<string>>(new Set())
  const [legs, setLegs] = useState<SlipLeg[]>([])
  const [stake, setStake] = useState('50')
  const [sportFilter, setSportFilter] = useState<string>('all')
  const [placementError, setPlacementError] = useState<string | null>(null)

  const toggleActive = (id: string) => {
    setActiveEventIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const minOdds = Number(status.data?.minOdds ?? '1.5')
  const maxOdds = Number(status.data?.maxOdds ?? '3.0')
  const maxStakePct = status.data?.maxStakePct ?? 0.02
  const startingBalance = Number(status.data?.startingBalance ?? '0')
  const maxStake = startingBalance > 0 ? startingBalance * maxStakePct : Infinity

  // Sports menu: start from the tier-allowed list, then hide buckets that
  // currently have zero events (NFL offseason, MMA off-day, etc.) so the
  // menu doesn't dangle dead entries. Falls back to event-derived sports if
  // the tier endpoint is unreachable.
  const sports = useMemo(() => {
    const countFor = (s: string) => (events.data ?? []).filter((e) => e.sport === s).length
    if (allowedSports.data && allowedSports.data.length > 0) {
      // While events are still loading, keep the full tier list (no flicker);
      // once events resolve, drop the empty buckets.
      if (events.isLoading) return allowedSports.data
      return allowedSports.data.filter((s) => countFor(s) > 0)
    }
    const set = new Set<string>()
    ;(events.data ?? []).forEach((e) => set.add(e.sport))
    return Array.from(set)
  }, [allowedSports.data, events.data, events.isLoading])

  const visibleEvents = (events.data ?? []).filter((e) => sportFilter === 'all' || e.sport === sportFilter)

  // Combined odds = product of per-leg odds (parlay payout multiplier).
  const combinedOdds = legs.reduce((acc, l) => acc * Number(l.oddsDecimal), 1)
  const stakeNum = Number(stake) || 0
  const potentialWin = stakeNum * combinedOdds
  const netProfit = potentialWin - stakeNum

  const stakeError = stakeNum > maxStake
    ? `Max stake per bet is ${fmtEur(maxStake)} (${(maxStakePct * 100).toFixed(1)}% of starting balance).`
    : null

  // Combined-odds rule: per challenge spec, the product of all legs must also
  // sit within [minOdds, maxOdds]. A 3-leg combo at 2.30 × 1.91 × 1.91 = 8.39
  // is outside the Pro tier max of 3.00 and is rejected here.
  const combinedOddsError = legs.length > 0 && (combinedOdds < minOdds || combinedOdds > maxOdds)
    ? `Combined odds ${combinedOdds.toFixed(2)} are outside the allowed range [${minOdds.toFixed(2)}, ${maxOdds.toFixed(2)}]. ` +
      (legs.length > 1 ? 'Remove a leg or pick lower-odds selections.' : '')
    : null

  // Users without an active challenge can't place bets at all.
  const noAccount = !status.data || Number(status.data.startingBalance) === 0

  const toggleLeg = (event: MarketEvent, marketType: string, sel: { id: string; name: string; oddsDecimal: string }) => {
    const odds = Number(sel.oddsDecimal)
    if (odds < minOdds || odds > maxOdds) {
      setPlacementError(`Odds ${sel.oddsDecimal} outside the allowed range [${minOdds.toFixed(2)}, ${maxOdds.toFixed(2)}] for your tier.`)
      return
    }
    setPlacementError(null)
    setLegs((prev) => {
      // Replace existing leg on the same event (one selection per event).
      const without = prev.filter((l) => l.eventId !== event.id)
      const existing = prev.find((l) => l.eventId === event.id && l.selectionId === sel.id)
      if (existing) return without // toggle off
      return [...without, {
        eventId:       event.id,
        eventLabel:    `${event.home} vs ${event.away}`,
        marketType,
        selectionId:   sel.id,
        selectionCode: sel.id.split(':').pop() ?? sel.id,
        selectionName: sel.name,
        oddsDecimal:   sel.oddsDecimal,
      }]
    })
  }

  const removeLeg = (selectionId: string) => {
    setLegs((prev) => prev.filter((l) => l.selectionId !== selectionId))
  }

  const clearSlip = () => {
    setLegs([])
    setPlacementError(null)
  }

  const submitBet = async () => {
    if (legs.length === 0) return
    if (stakeError) return
    setPlacementError(null)

    try {
      if (legs.length === 1) {
        const leg = legs[0]!
        await place.mutateAsync({
          eventId:     leg.eventId,
          marketId:    leg.marketType,
          selectionId: leg.selectionId,
          oddsDecimal: leg.oddsDecimal,
          stake,
        })
      } else {
        // Parlay: backend resolves as a single combined bet (all-or-nothing,
        // payout = stake × product(odds)). First leg also lives at the top of
        // the payload for backwards compat.
        const first = legs[0]!
        await place.mutateAsync({
          eventId:     first.eventId,
          marketId:    first.marketType,
          selectionId: first.selectionId,
          oddsDecimal: first.oddsDecimal,
          stake,
          legs: legs.map((l) => ({
            eventId:     l.eventId,
            marketId:    l.marketType,
            selectionId: l.selectionId,
            oddsDecimal: l.oddsDecimal,
          })),
        })
      }
      clearSlip()
      setStake('50')
    } catch (e) {
      setPlacementError(apiErrorMessage(e))
    }
  }

  return (
    <AppShell page="sportsbook" breadcrumb={<span className="topbar__breadcrumb-current">Sportsbook</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Sportsbook</span></p>
          <h1 className="page-header__title">Place a bet</h1>
          <p className="page-header__subtitle">
            Pick a market and add it to your slip. Combine multiple matches for higher payout.
          </p>
        </div>
        <div className="page-header__actions">
          <span className="chip">
            Odds {minOdds.toFixed(2)} – {maxOdds.toFixed(2)}
          </span>
          {startingBalance > 0 && (
            <span className="chip">
              Max stake {fmtEur(maxStake)}
            </span>
          )}
        </div>
      </header>

      <div className="sportsbook-layout">
        <aside className="sportsbook-layout__sports">
          <nav className="sport-nav" aria-label="Sports">
            <span className="sport-nav__group-label">Sports</span>
            <button type="button" className={`sport-nav__item ${sportFilter === 'all' ? 'sport-nav__item--active' : ''}`}
              onClick={() => setSportFilter('all')}>
              <SportIcon sport="all" />
              <span>All sports</span>
              <span className="sport-nav__count">{events.data?.length ?? 0}</span>
            </button>
            {sports.map((s) => (
              <button key={s} type="button"
                className={`sport-nav__item ${sportFilter === s ? 'sport-nav__item--active' : ''}`}
                onClick={() => setSportFilter(s)}>
                <SportIcon sport={s} />
                <span>{fmtSport(s)}</span>
                <span className="sport-nav__count">{events.data?.filter((e) => e.sport === s).length ?? 0}</span>
              </button>
            ))}
          </nav>
        </aside>

        <section className="sportsbook-layout__matches" aria-label="Upcoming events">
          {events.isLoading && <div style={{ color: 'var(--text-tertiary)' }}>Loading…</div>}
          {events.isError && (
            <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
              {apiErrorMessage(events.error)}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {visibleEvents.map((ev) => {
              const isActive = activeEventIds.has(ev.id)
              const inSlip = legs.some((l) => l.eventId === ev.id)
              return (
                <Fragment key={ev.id}>
                <button type="button" className="match-card"
                  onClick={() => toggleActive(ev.id)}
                  style={{
                    border: isActive ? '1px solid var(--color-primary)' : inSlip ? '1px solid var(--color-success)' : '1px solid var(--border-subtle)',
                    background: isActive ? 'var(--color-primary-subtle)' : 'var(--bg-surface)',
                    textAlign: 'left', cursor: 'pointer',
                  }}>
                  <div className="match-card__header">
                    <span className="match-card__league" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {ev.leagueLogo && <img src={ev.leagueLogo} alt="" width={16} height={16} style={{ objectFit: 'contain', flexShrink: 0 }} />}
                      {ev.league}
                    </span>
                    <span className="match-card__time">{fmtTime(ev.startsAt)}</span>
                  </div>
                  <div className="match-card__teams">
                    <span className="match-card__team" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {ev.homeLogo && <img src={ev.homeLogo} alt="" width={20} height={20} style={{ objectFit: 'contain', flexShrink: 0 }} />}
                      {ev.home}
                    </span>
                    {ev.liveScore ? (
                      <span className="match-card__score" style={{
                        fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--color-success-text)',
                        padding: '0 8px',
                      }}>
                        {ev.liveScore.home} – {ev.liveScore.away}
                      </span>
                    ) : (
                      <span className="match-card__vs">vs</span>
                    )}
                    <span className="match-card__team" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {ev.awayLogo && <img src={ev.awayLogo} alt="" width={20} height={20} style={{ objectFit: 'contain', flexShrink: 0 }} />}
                      {ev.away}
                    </span>
                  </div>
                  <div className="match-card__meta" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-tertiary)' }}>{fmtSport(ev.sport)}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                    {ev.status === 'live' ? (
                      <span style={{ color: 'var(--color-success-text)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-success-text)', display: 'inline-block' }} />
                        LIVE
                        {ev.liveScore?.minute != null && <span style={{ fontWeight: 500 }}>· {ev.liveScore.minute}'</span>}
                        {ev.liveScore?.period && ev.liveScore.minute == null && <span style={{ fontWeight: 500 }}>· {ev.liveScore.period}</span>}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', textTransform: 'capitalize' }}>{ev.status}</span>
                    )}
                    {inSlip && (<>
                      <span style={{ color: 'var(--color-success-text)' }}>·</span>
                      <span style={{ color: 'var(--color-success-text)', fontWeight: 600 }}>In slip</span>
                    </>)}
                  </div>
                </button>
                {isActive && (
                  <MatchMarkets ev={ev} minOdds={minOdds} maxOdds={maxOdds} legs={legs} onToggleLeg={toggleLeg} />
                )}
                </Fragment>
              )
            })}
            {events.data && visibleEvents.length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', padding: 'var(--space-5)', textAlign: 'center' }}>
                No events for this sport.
              </div>
            )}
          </div>
        </section>

        <aside className="sportsbook-layout__slip" aria-label="Bet slip">
          <div className="bet-slip">
            <header className="bet-slip__header">
              <span className="bet-slip__title">
                Bet slip
                {legs.length > 0 && <span className="bet-slip__count">{legs.length}</span>}
              </span>
              {legs.length > 0 && <button type="button" className="bet-slip__clear" onClick={clearSlip}>Clear all</button>}
            </header>

            {legs.length === 0 && (
              <div className="bet-slip__empty">
                <p>Pick a market to add it to your slip. Add picks from multiple matches for a combo.</p>
              </div>
            )}

            {legs.length > 0 && (
              <>
                <div className="bet-slip__selections">
                  {legs.map((leg) => (
                    <div key={leg.selectionId} className="selection">
                      <button type="button" className="selection__remove" aria-label="Remove" onClick={() => removeLeg(leg.selectionId)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M6 6 l12 12 M18 6 l-12 12"/>
                        </svg>
                      </button>
                      <span className="selection__match">{leg.eventLabel}</span>
                      <div className="selection__pick">
                        <span className="selection__pick-label">
                          {leg.selectionName}
                          <span className="selection__pick-market">{leg.marketType.toUpperCase()}</span>
                        </span>
                        <span className="selection__odds">{Number(leg.oddsDecimal).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <footer className="bet-slip__footer">
                  <div style={{ marginBottom: 12 }}>
                    <label className="field__label" htmlFor="bet-stake">Stake (€)</label>
                    <input id="bet-stake" className="field__input" type="number" step="0.01" min="0"
                      placeholder=""
                      value={stake} onChange={(e) => setStake(e.target.value)} />
                  </div>

                  <div className="bet-slip__totals">
                    <div className="bet-slip__total-row">
                      <span>Total stake</span>
                      <span className="bet-slip__total-value">{fmtEur(stakeNum)}</span>
                    </div>
                    {legs.length > 1 && (
                      <div className="bet-slip__total-row">
                        <span>Combined odds</span>
                        <span className="bet-slip__total-value" style={combinedOddsError ? { color: 'var(--color-danger-text)' } : undefined}>
                          {combinedOdds.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="bet-slip__total-row bet-slip__total-row--main">
                      <span>Potential return</span>
                      <span className="bet-slip__total-value">{fmtEur(potentialWin)}</span>
                    </div>
                    <div className="bet-slip__total-row">
                      <span>Net profit</span>
                      <span className="bet-slip__total-value">{fmtEur(netProfit)}</span>
                    </div>
                  </div>

                  {(stakeError || combinedOddsError || noAccount || placementError) && (
                    <div className="selection__error" role="alert" style={{ marginTop: 12 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3 L22 20 H2 z"/><path d="M12 10 v4 M12 18 v.01"/>
                      </svg>
                      <span>{stakeError || combinedOddsError || (noAccount ? 'No active challenge. Pick a tier on the Challenge page before placing a bet.' : '') || placementError}</span>
                    </div>
                  )}

                  <button type="button" className="btn btn--primary bet-slip__place"
                    disabled={place.isPending || !!stakeError || !!combinedOddsError || noAccount || stakeNum <= 0}
                    onClick={submitBet}>
                    {place.isPending ? 'Placing…' : legs.length === 1 ? 'Place bet' : `Place ${legs.length}-leg combo`}
                  </button>

                  {status.data && status.data.betsRequired > 0 && (
                    <div className="bet-slip__daily" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      <span>Phase bets</span>
                      <span className="bet-slip__daily-value">
                        {status.data.betsPlaced} settled
                        {status.data.betsPending > 0 ? ` + ${status.data.betsPending} pending` : ''}
                        {' / '}{status.data.betsRequired} minimum
                      </span>
                    </div>
                  )}
                </footer>
              </>
            )}
          </div>
        </aside>
      </div>
    </AppShell>
  )
}
